import {
  AppstoreOutlined,
  CalendarOutlined,
  DownOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  HomeOutlined,
  LeftOutlined,
  PlayCircleOutlined,
  ProfileOutlined,
  ReloadOutlined,
  RightOutlined,
  SettingOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  Button, Tabs, Row, Col, Card, DatePicker, Empty, Form, Input, Pagination, Select, Segmented, Space, Typography, message,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import http from "@/utils/http";
import api from "@/utils/api";
import ReportCreator from "./ReportCreator";
import "./index.css";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const money = (value) => {
  const amount = Number(value || 0);
  if (amount === 0) return "-";
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 2,
  }).format(Math.abs(amount));
  return amount < 0 ? `$(${formatted.replace("$", "")})` : formatted;
};

const asList = (response) => Array.isArray(response) ? response : (response?.data || []);
const propertyCode = (property) => property?.propCode || property?.prop || "";
const initialValues = () => ({
  dateRange: [dayjs().startOf("month"), dayjs()],
  prop: "0001-ZZZZ",
  type: "prop",
  reportType:"mortgage",
  cityList: [],
  propTypeList: [],
  trustIdList: [],
  ownerIdList: [],
  glChartNameId: undefined,
});

const RESReport = () => {
  const [form] = Form.useForm();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [reportOptions, setReportOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [propTypeOptions, setPropTypeOptions] = useState([]);
  const [trustOptions, setTrustOptions] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [glChartOptions, setGlChartOptions] = useState([]);
  const [viewMode, setViewMode] = useState("separate");
  const [selectedPropId, setSelectedPropId] = useState(null);
  const [propertyPage, setPropertyPage] = useState(1);
  const tableRef = useRef(null);
  const loaded = useRef({});

  useEffect(() => {
    form.setFieldsValue(initialValues());
    // loadReports();
  }, [form]);

  const normalizeOption = (item, fallbackLabelKeys = []) => {

    if (typeof item === "string" || typeof item === "number") return { value: item, label: item };
    const value = item?.value ?? item?.id ?? item?.reportNameId ?? item?.trustId ?? item?.ownerId ?? item?.code ?? item?.label;
    const label = fallbackLabelKeys.map((key) => item?.[key]).find(Boolean) ?? item?.label ?? item?.name ?? value;
    // if（）{
    //   value = item?.code
    // }
    return value === undefined || value === null ? null : { value, label };
  };

  const normalizePropTypeOption = (item, fallbackLabelKeys = []) => {

    if (typeof item === "string" || typeof item === "number") return { value: item, label: item };
    const value = item?.code;
    const label = fallbackLabelKeys.map((key) => item?.[key]).find(Boolean) ?? item?.label ?? item?.name ?? value;
    // if（）{
    //   value = item?.code
    // }
    return value === undefined || value === null ? null : { value, label };
  };

  const loadReports = async () => {
    if (loaded.current.reports) return;
    try {
      const options = asList(await http.get(api.getReportConfigNames, { params: { reportType: "res" } }))
        .map((item) => normalizeOption(item, ["reportName"]))
        .filter(Boolean);
      setReportOptions(options);
      loaded.current.reports = true;
      if (options.length && !form.getFieldValue("report")) form.setFieldValue("report", options[0].value);
    } catch (_error) {
      message.warning("Report options could not be loaded.");
    }
  };

  const lazyLoad = async (key, apiUrl, setter, mapper) => {
    if (loaded.current[key]) return;
    try {
      const options = asList(await http.get(apiUrl)).map(mapper).filter(Boolean);
      setter(options);
      loaded.current[key] = true;
    } catch (_error) {
      message.warning("Filter options could not be loaded.");
    }
  };

  const payload = async () => {
    const values = await form.validateFields();
    const [dateFrom, dateTo] = values.dateRange || [];
    return {
      report: values.report,
      dateFrom: dateFrom?.format("YYYY-MM-DD"),
      dateTo: dateTo?.format("YYYY-MM-DD"),
      prop: values.prop?.trim() || "",
      type: values.type || "prop",
      cityList: values.cityList || [],
      propTypeList: values.propTypeList || [],
      trustIdList: values.trustIdList || [],
      ownerIdList: values.ownerIdList || [],
      glChartNameId: values.glChartNameId,
      reportType:"mortgage",
    };
  };

  const reset = () => {
    form.setFieldsValue({ ...initialValues(), report: reportOptions[0]?.value });
    setReport(null);
    setSelectedPropId(null);
    setPropertyPage(1);
  };

  const run = async () => {
    setLoading(true);
    try {
      const response = await http.post(api.getResReportShow, await payload());
      if (response?.code !== "1") throw new Error(response?.msg || "Unable to run RES report.");
      setReport(response.data);
      const firstProp = response.data?.columns?.[0]?.propId ?? null;
      setSelectedPropId(firstProp);
      setViewMode(response.data?.type === "consolidate" ? "combined" : "separate");
      setPropertyPage(1);
    } catch (error) {
      message.error(error?.message || "Please review the report filters.");
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    setExporting(format);
    try {
      const endpoint = format === "pdf" ? api.getResReportToPdf : api.getResReportToCsv;
      const blob = await http.post(endpoint, await payload(), { responseType: "blob" });
      if (!(blob instanceof Blob)) throw new Error("Invalid file response");
      if (blob.type.includes("application/json")) {
        throw new Error((JSON.parse(await blob.text()))?.msg || "Export failed.");
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `RES_Report_${dayjs().format("YYYYMMDD_HHmmss")}.${format}`;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      message.error(error?.message || "Export failed.");
    } finally {
      setExporting("");
    }
  };

  const properties = useMemo(() => report?.columns || [], [report]);
  const selectedIndex = Math.max(0, properties.findIndex((property) => property.propId === selectedPropId));
  const selectedProperty = properties[selectedIndex];
  const pageSize = 24;
  const visibleProperties = viewMode === "separate" ? (selectedProperty ? [selectedProperty] : [])
    : properties.slice((propertyPage - 1) * pageSize, propertyPage * pageSize);
  const visibleRows = viewMode === "separate" && selectedProperty
    ? (report?.rowsByProp?.[selectedProperty.propId] || report?.rows || [])
    : (report?.rows || []);

  const mortgageMap = useMemo(() => report?.mortgageMap || [], [report])
  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollTo({ top: 0, left: 0 });
  }, [viewMode, selectedPropId, propertyPage, report]);

  return (
    <main className="res-report-page">
      <section className={`res-report-layout${filtersCollapsed ? " res-report-layout-collapsed" : ""}`}>
        <Card className="res-report-filter-card" style={filtersCollapsed ? { display: "none" } : undefined} title={(
          <div className="res-report-filter-header">
            <div className="res-report-title-row">
              <span>Report filters</span>
              <Button className="res-report-collapse" type="text" icon={<UpOutlined />} onClick={() => setFiltersCollapsed(true)}>Hide filters</Button>
            </div>
            <div className="res-report-actions">
              <Button icon={<ReloadOutlined />} onClick={reset}>Reset</Button>
              <Button type="primary" icon={<PlayCircleOutlined />} loading={loading} onClick={run}>Run Report</Button>
            </div>
          </div>
        )}>
          <Form form={form} layout="vertical">
            {/* <Form.Item name="report" label="Report" rules={[{ required: true, message: "Select a report" }]}>
              <Select showSearch optionFilterProp="label" options={reportOptions} onDropdownVisibleChange={(open) => open && loadReports()} />
            </Form.Item> */}
            <Form.Item name="dateRange" label="From/To Date" rules={[{ required: true, message: "Select a date range" }]}>
              <RangePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="prop" label="Prop" rules={[{ required: true, message: "Input Prop" }]}>
              <Input.TextArea className="res-report-property-input" rows={3} placeholder="e.g. 0001, 1005-1010" />
            </Form.Item>
            <Form.Item name="cityList" label="City">
              <Select mode="multiple" allowClear showSearch optionFilterProp="label" maxTagCount={4} options={cityOptions} onDropdownVisibleChange={(open) => open && lazyLoad("cities", api.getPropCityDict, setCityOptions, (item) => normalizeOption(item, ["city", "name"]))} />
            </Form.Item>
            <Form.Item name="propTypeList" label="Property Type">
              <Select mode="multiple" allowClear showSearch optionFilterProp="label" maxTagCount={4} options={propTypeOptions} onDropdownVisibleChange={(open) => open && lazyLoad("propTypes", api.getPropTypeList, setPropTypeOptions, (item) => normalizePropTypeOption(item, ["name", "propType"]))} />
            </Form.Item>
            <Form.Item name="trustIdList" label="Trust">
              <Select mode="multiple" allowClear showSearch optionFilterProp="label" maxTagCount={4} options={trustOptions} onDropdownVisibleChange={(open) => open && lazyLoad("trusts", api.getTrustDictList, setTrustOptions, (item) => normalizeOption(item, ["trustNameTitle", "trustName", "trust"]))} />
            </Form.Item>
            <Form.Item name="ownerIdList" label="Owner">
              <Select mode="multiple" allowClear showSearch optionFilterProp="label" maxTagCount={4} options={ownerOptions} onDropdownVisibleChange={(open) => open && lazyLoad("owners", api.getOwnerDictList, setOwnerOptions, (item) => normalizeOption(item, ["titleName", "ownerName", "name"]))} />
            </Form.Item>
            <Form.Item name="glChartNameId" label="GL Chart Name">
              <Select allowClear showSearch optionFilterProp="label" options={glChartOptions} onDropdownVisibleChange={(open) => open && lazyLoad("glCharts", api.operatingStatementMonthsGlChartNameDict, setGlChartOptions, (item) => normalizeOption(item, ["label", "glChartName"]))} />
            </Form.Item>
            <Form.Item name="type" label="Group By" rules={[{ required: true, message: "Select Group By" }]}>
              <Select options={[{ label: "Prop", value: "prop" }, { label: "Consolidate Sum", value: "consolidate" }]} />
            </Form.Item>
          </Form>
          <div className="res-report-creator-link">
            <div className="res-report-creator-heading"><SettingOutlined /><strong>Report Creator</strong></div>
            <p>Groups, GL ranges and display order.</p>
            <Button block onClick={() => setCreatorOpen(true)}>Configure rules <RightOutlined /></Button>
          </div>
        </Card>
        <div className="res-report-preview-column">
          {filtersCollapsed && <div className="res-report-show-filters"><Button className="res-report-collapse" type="text" icon={<DownOutlined />} onClick={() => setFiltersCollapsed(false)}>Show filters</Button></div>}
          <Card className="res-report-result-card" title={<div className="res-report-preview-title"><span className="res-report-mark"><FileTextOutlined /></span><div>Report preview<small>RES · Rental real estate</small></div></div>} extra={(
            <Space>
              <Button icon={<FileTextOutlined />} loading={exporting === "csv"} onClick={() => download("csv")}>CSV</Button>
              <Button type="primary" icon={<FilePdfOutlined />} loading={exporting === "pdf"} onClick={() => download("pdf")}>PDF</Button>
            </Space>
          )}>
            {report ? <>
              <div className="res-report-summary">
                <div className="res-report-period"><span className="res-report-property-count">{properties.length} properties</span><span><CalendarOutlined /> {dayjs(report.dateFrom).format("MMM D, YYYY")} - {dayjs(report.dateTo).format("MMM D, YYYY")}</span></div>
                <Segmented value={viewMode} onChange={setViewMode} options={[{ label: "Combined", icon: <AppstoreOutlined />, value: "combined" }, { label: "By property", icon: <ProfileOutlined />, value: "separate" }]} />
              </div>
              <div className={`res-report-surface res-report-mode-${viewMode}`}>
                {viewMode === "separate" && selectedProperty && <>
                  <div className="res-report-property-nav">
                    {/* <span className="res-report-nav-label">PROPERTY</span> */}
                    {/* <Select showSearch optionFilterProp="label" value={selectedProperty.propId} onChange={setSelectedPropId} options={properties.map((p) => ({ value: p.propId, label: `${propertyCode(p)}` }))} /> */}
                    {/* <Text className="res-report-nav-counter" type="secondary">{selectedIndex + 1} of {properties.length}</Text>
                    <Button icon={<LeftOutlined />} disabled={selectedIndex === 0} onClick={() => setSelectedPropId(properties[selectedIndex - 1].propId)} />
                    <Button icon={<RightOutlined />} disabled={selectedIndex === properties.length - 1} onClick={() => setSelectedPropId(properties[selectedIndex + 1].propId)} /> */}
                    <Tabs
                      className="OSMRTabs"
                      items={properties.map(item => {
                        return {
                           key: item.propId,
                           label: propertyCode(item),
                           forceRender: true,
                        }
                      })}
                      onChange={setSelectedPropId}
                      animated
                      more={{
                        trigger: 'click',
                        placement: 'bottomLeft',
                      }}
                    />
                  </div>
                  <div className="res-report-property-detail">
                    <div className="res-report-property-identity"><span className="res-report-property-icon"><HomeOutlined /></span><div><strong>{propertyCode(selectedProperty)}<span>{selectedProperty.address}</span></strong><p>{selectedProperty.cityStateZip}</p></div></div>
                    <div className="res-report-property-facts"><div><span>Status</span><strong>{selectedProperty.propertyStatus || "-"}</strong></div><div><span>Property type</span><strong>{selectedProperty.propType || "-"}</strong></div><div><span>Units</span><strong>{selectedProperty.units ?? "-"}</strong></div><div><span>Purchase date / price</span><strong>{selectedProperty.purchaseDatePrice || "-"}</strong></div></div>
                  </div>
                </>}
                {properties.length ? <div ref={tableRef} className={`res-report-table-scroll ${viewMode}`} tabIndex={0}>
                  <table className="res-report-data-table">
                    <thead><tr><th scope="col">Description</th>{visibleProperties.map((property) => <th key={property.propId} scope="col" className="res-report-money-cell">
                      {viewMode === "combined" && <div className="res-report-column-property"><strong>{propertyCode(property)}</strong><span>{property.address || property.propertyStatus || "-"}</span><small>{property.cityStateZip}</small></div>}
                      <span>Amount</span>
                    </th>)}</tr></thead>
                    <tbody>{visibleRows.map((row, index) => <tr key={`${row.rowType}-${index}`} className={`res-report-row-${String(row.rowType || "").toLowerCase()}`}>
                      <th scope="row">{row.label}</th>{visibleProperties.map((property) => <td key={property.propId} className="res-report-money-cell">{["GROUP", "SPACER"].includes(row.rowType) ? "" : money(row.amounts?.[property.propId])}</td>)}
                    </tr>)}</tbody>
                  </table>
                  <div style={{borderTop: '2px solid #2a6788', marginTop: 20}}>
                    {mortgageMap[selectedPropId] && [
                      {label: 'Loan Number', value: 'loanNumber', style: {color: '#17354f', backgroundColor: '#eaf3f8', borderBottom: '2px solid #2a6788', fontSize: '13px', fontWeight: 700}},
                      {label: 'Ending payments', value: 'loanBalance', isAmount: true},
                      {label: 'Lender', value: 'lender'},
                      {label: 'DSC', value: 'dscr', isAmount: true},
                      {label: 'Interest Rate', value: 'interestRate'},
                      {label: 'GL', value: 'glStr'},
                      {label: 'LoanDate', value: 'loanDateStr'},
                      {label: 'Maturity Date', value: 'loanEndDateStr'},
                    ].map(item => {
                      return <>
                        <Row gutter={[16, 16]} style={ item?.style ? item.style : {color: '#384b63', borderBottom: '1px solid #edf1f6', backgroundColor:'#f5f8fb', fontSize: '13px',}}>
                          <Col style={{padding: '9px 20px', flex: '1', borderRight: '1px solid #e3eaf3'}}>{item.label}</Col>
                          {mortgageMap[selectedPropId]?.map(v => {
                            return <Col style={{padding: '9px 20px', flex: '1', borderRight: '1px solid #e3eaf3'}}>{item.isAmount ? money(v[item.value]) : v[item.value]}</Col>
                          })}
                        </Row>
                      </>
                    }) || null}
                  </div>
                </div> : <Empty description="No properties found" />}
              </div>
              <div className="res-report-footer"><Text type="secondary">PDF exports one property per page; consolidated reports export one consolidated page.</Text>{viewMode === "combined" && <Pagination size="small" current={propertyPage} pageSize={pageSize} total={properties.length} showSizeChanger={false} onChange={setPropertyPage} />}</div>
            </> : <Empty className="res-report-empty" description="Run the RES report to review data before exporting." />}
          </Card>
        </div>
      </section>
      <ReportCreator open={creatorOpen} onClose={() => setCreatorOpen(false)} onSaved={() => setReport(null)} />
    </main>
  );
};

export default RESReport;
